import * as React from "react"
import {
  Box,
  ButtonGroup,
  Button,
  Divider,
  Flex,
  Spinner,
  Image,
  Stack,
  Text,
  Progress,
  SimpleGrid,
  MenuList,
  MenuButton,
  MenuItem,
  Menu,
  Textarea,
  Heading,
  Center,
  HStack,
  useDisclosure,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Avatar,
  IconButton,
} from "@chakra-ui/react"
import { useNavigate, useParams } from "react-router-dom"
import { useQuery, useMutation } from "react-query"
import { FiChevronDown, FiEdit } from "react-icons/fi"
import dayjs from "dayjs"
import { FaCircle } from "react-icons/fa"
import relativeTime from "dayjs/plugin/relativeTime"
import { BikeEntity } from "../lib/types/bike"
import { fetcher } from "../lib/fetcher"
import { NoData } from "../components/NoData"
import { humanize } from "../lib/helpers/text"
import { getBikeStateOptions } from "../lib/static/bike"
import { useOrgs } from "../components/providers/OrgProvider"
import { useToast } from "../lib/hooks/useToast"
import { LocationLink } from "../components/LocationLink"
import { TOPBAR_HEIGHT } from "../lib/config"
import { getDistance } from "../lib/helpers/distance"
import { useOnChangeOrgRedirect } from "../lib/hooks/useOnChangeOrgRedirect"
import { CustomTags } from "../components/CustomTags"
import { useFeatFlags } from "../components/providers/FeatFlagProvider"
import { useMe } from "../components/providers/MeProvider"
import { BikeTracker } from "../components/BikeTracker"
import { BikeLock } from "../components/BikeLock"
import { LiveChaseModal } from "../components/LiveChaseModal"
import { BikeOrgSwitcher } from "../components/BikeOrgSwitcher"
import { OrgEntity } from "../lib/types/org"
import { isSupervisor } from "../lib/helpers/role"

dayjs.extend(relativeTime)

interface TransitionResponse {
  results: { timestamp: string }[]
}
interface StatResponse {
  total_distance: number
}

export const Bike = () => {
  useOnChangeOrgRedirect("/assets")

  const { id } = useParams()
  const navigate = useNavigate()
  const { tags, live_chase, bike_sn_as_name_extension, bike_admin_features, asset_deployed_state } =
    useFeatFlags()
  const me = useMe()
  const toast = useToast()

  const noteTextAreaRef = React.useRef<HTMLTextAreaElement>(null)
  const descriptionTextAreaRef = React.useRef<HTMLTextAreaElement>(null)
  const retireConfirmModalProps = useDisclosure()
  const liveChaseModalProps = useDisclosure()
  const orgsModalProps = useDisclosure()

  const { currentOrgs } = useOrgs()
  const [note, setNote] = React.useState("")
  const [isEditingNote, setIsEditingNote] = React.useState(false)
  const [description, setDescription] = React.useState("")
  const [isEditingDescription, setIsEditingDescription] = React.useState(false)

  const { data, isLoading, refetch } = useQuery<BikeEntity, string>(`bicycles/${id}/`)

  const { data: transitionData, refetch: refetchTransition } = useQuery<TransitionResponse, string>(
    `bicycles/${id}/transitions/`,
    { staleTime: Infinity },
  )
  const bike = data
  const bikeOrg = currentOrgs.find(({ url }) => url === bike?.organization)

  const { mutateAsync: updateNote, isLoading: notesLoading } = useMutation<any, unknown, { note: string }>(
    (data) => fetcher(`bicycles/${id}/`, { body: JSON.stringify(data), method: "PATCH" }),
  )

  const { mutateAsync: updateDescription, isLoading: descriptionLoading } = useMutation<
    any,
    unknown,
    { description: string }
  >((data) => fetcher(`bicycles/${id}/`, { body: JSON.stringify(data), method: "PATCH" }))

  const { mutateAsync: updateBike, isLoading: updateBikeLoading } = useMutation<
    any,
    unknown,
    { type: string }
  >((data) => fetcher(`bicycles/${id}/actions/`, { body: JSON.stringify(data), method: "post" }))

  const { mutateAsync: updateBikeOrg, isLoading: changeOrgBikeLoading } = useMutation<
    any,
    unknown,
    { organization: string }
  >((data) => fetcher(`bicycles/${id}/`, { body: JSON.stringify(data), method: "PATCH" }))

  React.useEffect(() => {
    if (data?.note) setNote(data.note)
    if (data?.description) setDescription(data.description)
  }, [data])

  const { data: statsData } = useQuery<StatResponse, string>(`bicycles/${id}/stats/`, { staleTime: Infinity })

  const handleUpdateState = async (type: string) => {
    try {
      const res = await updateBike({ type })
      if (res?.detail?.non_field_errors) {
        toast({ description: res?.detail?.non_field_errors[0].message, status: "error" })
      } else if (res?.detail?.actions) {
        toast({
          description:
            "Sorry, you are not allowed to change from the current state, please contact Noa Support",
          status: "error",
        })
      } else if (res) {
        toast({ description: "State updated!" })
        refetch()
        refetchTransition()
      } else {
        toast({ description: "Error updating state", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handleUpdateNote = async () => {
    if (!note) return
    try {
      const res = await updateNote({ note })
      if (res?.detail?.non_field_errors) {
        toast({ description: res?.detail?.non_field_errors[0].message, status: "error" })
      } else if (res) {
        toast({ description: "Notes updated!" })
        await refetch()
        setIsEditingNote(false)
      } else {
        toast({ description: "Error updating note", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }
  const handleCancelNote = () => {
    if (!data?.note) return
    setNote(data.note)
    setIsEditingNote(false)
  }

  const handleUpdateDescription = async () => {
    if (!description) return
    try {
      const res = await updateDescription({ description })
      if (res?.detail?.non_field_errors) {
        toast({ description: res?.detail?.non_field_errors[0].message, status: "error" })
      } else if (res) {
        toast({ description: "Description updated!" })
        await refetch()
        setIsEditingDescription(false)
      } else {
        toast({ description: "Error updating description", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }
  const handleCancelDescription = () => {
    if (!data?.description) return
    setDescription(data.description)
    setIsEditingDescription(false)
  }

  const handleChangeOrg = async (org: OrgEntity) => {
    try {
      if (bike?.devices?.tracker || bike?.devices?.lock) {
        toast({
          description: "Cannot move asset with paired devices",
          status: "error",
        })
        return
      }

      const resBike = await updateBikeOrg({ organization: org.url })
      if (resBike?.detail?.non_field_errors) {
        toast({ description: `Asset: ${resBike?.detail?.non_field_errors[0].message}`, status: "error" })
      } else if (!resBike) {
        toast({ description: "Error changing asset organization", status: "error" })
      }

      if (resBike && !resBike?.detail?.non_field_errors) toast({ description: "Organization changed!" })

      await refetch()
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  return (
    <Box overflow="scroll" h="100%">
      {isLoading && (
        <Center h={400}>
          <Spinner />
        </Center>
      )}

      {!isLoading && !bike && (
        <Center h={400}>
          <NoData>Asset not found</NoData>
        </Center>
      )}

      {!isLoading && bike && (
        <Stack spacing={0}>
          <Flex align="center" justify="space-between" p={6} h={TOPBAR_HEIGHT}>
            <Heading as="h3" color="gray.700">
              {bike.name}
            </Heading>
          </Flex>

          <Stack p={6} spacing={6} w="100%">
            <SimpleGrid spacing={8} columns={{ base: 1, md: 2, lg: 3 }}>
              <Stack spacing={4}>
                <Image src={bike.image_url} w={200} objectFit="contain" />
                <Stack>
                  {bikeOrg && (
                    <>
                      {currentOrgs.length > 1 && (
                        <>
                          <Flex
                            align="center"
                            onClick={() => isSupervisor(me.roles) && orgsModalProps.onOpen()}
                            cursor={isSupervisor(me.roles) ? "pointer" : "default"}
                          >
                            {changeOrgBikeLoading ? (
                              <Spinner />
                            ) : (
                              <>
                                <Avatar mr={2} name={bikeOrg.name} src={bikeOrg.icon} size="xs" />
                                <Text whiteSpace="nowrap" fontSize="0.8rem">
                                  {bikeOrg.name}
                                </Text>
                              </>
                            )}
                          </Flex>
                          {orgsModalProps.isOpen && (
                            <BikeOrgSwitcher org={bikeOrg} onSelect={handleChangeOrg} {...orgsModalProps} />
                          )}
                        </>
                      )}
                      <Text color="gray.700" fontSize="0.8rem">
                        Distance ridden:{" "}
                        <Text as="span" color="gray.900" fontWeight={700}>
                          {getDistance(statsData?.total_distance, bikeOrg.preference.unit_system)}
                        </Text>
                      </Text>
                    </>
                  )}
                  {bike.serial_number &&
                    bike_sn_as_name_extension?.visible &&
                    bike.name.toUpperCase() !== bike.serial_number.toUpperCase() && (
                      <Text color="gray.700" fontSize="0.8rem">
                        Serial number:{" "}
                        <Text as="span" color="gray.900" fontWeight={700}>
                          {bike.serial_number}
                        </Text>
                      </Text>
                    )}
                  {bike.description && (
                    <Text color="gray.700" fontSize="0.8rem">
                      Description:{" "}
                      <Text as="span" color="gray.900" fontWeight={700}>
                        {bike.description}
                      </Text>
                    </Text>
                  )}
                  {bike.bleid && (
                    <Text color="gray.700" fontSize="0.8rem">
                      Tracker ID:{" "}
                      <Text as="span" color="gray.900" fontWeight={700}>
                        {bike.bleid || "N/A"}
                      </Text>
                    </Text>
                  )}
                </Stack>
                {bike.latitude && bike.longitude && (
                  <LocationLink obj={{ latitude: bike.latitude, longitude: bike.longitude }} />
                )}

                <HStack flexWrap="wrap">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/assets/${bike.uuid}/trips`)}>
                    Trips
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/assets/${bike.uuid}/alerts`)}>
                    Alerts
                  </Button>
                  {live_chase.visible && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={liveChaseModalProps.onOpen}
                      leftIcon={<Box as={FaCircle} boxSize="10px" color="red.500" />}
                    >
                      Live chase
                    </Button>
                  )}
                  {live_chase.visible && <LiveChaseModal bikeId={bike.uuid} {...liveChaseModalProps} />}
                  <a
                    href={`https://docs.google.com/forms/d/e/1FAIpQLSfS9AZUri_eEm6BLwfsUnRQaosmiKmr7DdQoagIng2qtOgm9A/viewform?usp=pp_url&entry.814125190=${
                      me.email
                    }&entry.1459092929=${me.display_name}&entry.2081821742=${
                      bike.devices?.tracker?.bleid
                    }&entry.1142341770=${bike.name || bike.serial_number}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="outline" size="sm">
                      Report a problem
                    </Button>
                  </a>
                </HStack>
              </Stack>
              <Stack spacing={2}>
                <Text fontWeight="600">State</Text>
                {!bike.state || bike.state === "unknown" ? (
                  "Unknown"
                ) : (
                  <Box>
                    <Menu>
                      <MenuButton as={Button} size="sm" minW={170} isLoading={updateBikeLoading}>
                        <Flex align="center" justify="space-between">
                          <Text>{humanize(bike.state)}</Text>
                          <Box as={FiChevronDown} />
                        </Flex>
                      </MenuButton>
                      <MenuList>
                        {getBikeStateOptions(
                          bike.state,
                          me.roles,
                          bike_admin_features?.visible,
                          asset_deployed_state?.visible,
                        ).map((opt, i) => (
                          <MenuItem
                            key={opt.value + i}
                            onClick={() => {
                              if (opt.value === "retired") {
                                retireConfirmModalProps.onOpen()
                              } else {
                                handleUpdateState(opt.value)
                              }
                            }}
                            isDisabled={updateBikeLoading}
                          >
                            <Box as={opt.icon} boxSize="14px" mr={2} />
                            {opt.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                    <Modal {...retireConfirmModalProps} isCentered>
                      <ModalOverlay zIndex={100}>
                        <ModalContent>
                          <ModalHeader>Retire asset</ModalHeader>
                          <ModalCloseButton />
                          <ModalBody>Are you sure you want to retire this asset?</ModalBody>
                          <ModalFooter>
                            <Button
                              colorScheme="red"
                              onClick={() => {
                                handleUpdateState("retired")
                                retireConfirmModalProps.onClose()
                              }}
                            >
                              Retire
                            </Button>
                          </ModalFooter>
                        </ModalContent>
                      </ModalOverlay>
                    </Modal>
                  </Box>
                )}
                {transitionData?.results?.[0] && (
                  <Text color="gray.700" fontSize="0.8rem" mb={4}>
                    Changed {dayjs(transitionData.results[0].timestamp).fromNow()}
                  </Text>
                )}

                <Stack py={2} maxW={300} spacing={2}>
                  <Text fontWeight="600">Device</Text>
                  <Flex align="center">
                    <Text color="gray.700" fontSize="0.8rem">
                      {Math.ceil(bike.state_of_charge || 0)}%
                    </Text>
                    <Progress
                      size="sm"
                      ml={1}
                      h="5px"
                      borderRadius="md"
                      w="100%"
                      colorScheme={
                        !bike.state_of_charge || bike.state_of_charge < 10
                          ? "red"
                          : bike.state_of_charge < 50
                          ? "orange"
                          : "green"
                      }
                      value={bike.state_of_charge || 0}
                    />
                  </Flex>
                  <Text color="gray.700" fontSize="0.8rem">
                    Last updated:{" "}
                    {bike.last_cellular_update ? dayjs(bike.last_cellular_update).fromNow() : "N/A"}
                  </Text>
                </Stack>
              </Stack>

              <Stack spacing={6}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} align="center">
                    <Text fontWeight="600">Description</Text>
                    {!isEditingDescription ? (
                      <IconButton
                        aria-label="edit description"
                        icon={<FiEdit />}
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setIsEditingDescription(true)
                          descriptionTextAreaRef.current?.focus()
                        }}
                      />
                    ) : (
                      <ButtonGroup spacing={1}>
                        <Button
                          onClick={handleCancelDescription}
                          colorScheme="red"
                          size="xs"
                          isDisabled={descriptionLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleUpdateDescription}
                          colorScheme="green"
                          size="xs"
                          isDisabled={descriptionLoading}
                          isLoading={descriptionLoading}
                        >
                          Save
                        </Button>
                      </ButtonGroup>
                    )}
                  </Stack>
                  <Textarea
                    maxW={300}
                    ref={descriptionTextAreaRef}
                    readOnly={!isEditingDescription}
                    p={2}
                    h="100px"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Stack>

                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} align="center">
                    <Text fontWeight="600">Notes</Text>
                    {!isEditingNote ? (
                      <IconButton
                        aria-label="edit notes"
                        icon={<FiEdit />}
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setIsEditingNote(true)
                          noteTextAreaRef.current?.focus()
                        }}
                      />
                    ) : (
                      <ButtonGroup spacing={1}>
                        <Button
                          onClick={handleCancelNote}
                          colorScheme="red"
                          size="xs"
                          isDisabled={notesLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleUpdateNote}
                          colorScheme="green"
                          size="xs"
                          isDisabled={notesLoading}
                          isLoading={notesLoading}
                        >
                          Save
                        </Button>
                      </ButtonGroup>
                    )}
                  </Stack>
                  <Textarea
                    maxW={300}
                    ref={noteTextAreaRef}
                    readOnly={!isEditingNote}
                    p={2}
                    h="120px"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Stack>
              </Stack>
            </SimpleGrid>

            <Divider />

            <SimpleGrid w="100%" columns={{ sm: 2, md: 3 }}>
              {tags.visible && (
                <Stack px={6} spacing={2}>
                  <Text fontWeight="600" textAlign="center">
                    Tags
                  </Text>
                  <CustomTags resourceUrl={bike.url} assignedTags={bike.tags} refetchResourceTags={refetch} />
                </Stack>
              )}

              <BikeTracker bike={bike} refetch={refetch} />

              <BikeLock bike={bike} refetch={refetch} />
            </SimpleGrid>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
